import { INDEX_BASE_OPTIONS, INDEX_ROUNDING_OPTIONS, RPI_LINK_MODES, assumptionDefinitionFor, parseIndexKey, } from './registry.js';
/**
 * Names that cannot be an index's, because the resolved rates travel as a
 * JSON OBJECT keyed by name — on the wire, in a stored run's stamp, and into
 * the engine. A key like `__proto__` is not merely awkward: assigned to a
 * plain object it changes the object's prototype instead of adding a figure,
 * and read from one it returns something that is not a rate at all.
 *
 * Two defences, both kept: these names are refused at validation (here), and
 * every map of rates is built and read as OWN properties only, so a name that
 * somehow reached storage still cannot reach a prototype. Matching is on the
 * trimmed, case-folded name, the same form uniqueness uses.
 */
const RESERVED_INDEX_NAMES = new Set([
    '__proto__',
    'constructor',
    'prototype',
    '__definegetter__',
    '__definesetter__',
    '__lookupgetter__',
    '__lookupsetter__',
    'hasownproperty',
    'isprototypeof',
    'propertyisenumerable',
    'tostring',
    'tolocalestring',
    'valueof',
]);
export function normaliseIndexName(name) {
    return name.trim().toLowerCase();
}
/** True when a name could not safely be an own key of a rates object. */
export function isUnsafeIndexName(name) {
    return RESERVED_INDEX_NAMES.has(normaliseIndexName(name));
}
/**
 * A rates object built so no key can reach a prototype: `fromEntries` defines
 * own properties (it never invokes a setter), and the result is handed on as
 * the plain object the wire contract and the engine expect.
 */
export function indexRateRecord(entries) {
    return Object.fromEntries(entries.filter(([name]) => !isUnsafeIndexName(name)));
}
/** Own-property read of a rates object — never an inherited member. */
export function readIndexRate(rates, name) {
    if (rates === undefined || isUnsafeIndexName(name))
        return undefined;
    return Object.prototype.hasOwnProperty.call(rates, name)
        ? rates[name]
        : undefined;
}
const pct = (bps) => {
    const sign = bps < 0 ? '-' : '';
    const abs = Math.abs(bps);
    return `${sign}${Math.trunc(abs / 100)}.${String(abs % 100).padStart(2, '0')}%`;
};
/**
 * Round to a whole number of `step` basis points, TIES AWAY FROM ZERO (§5.1).
 * Integer arithmetic only: 25 bps to the nearest 10 is 30, −25 is −30.
 */
export function roundBpsTo(valueBps, step) {
    if (step <= 1)
        return valueBps;
    const sign = valueBps < 0 ? -1 : 1;
    const abs = Math.abs(valueBps);
    const remainder = abs % step;
    const down = abs - remainder;
    return sign * (remainder * 2 >= step ? down + step : down);
}
const ROUNDING_STEP = Object.freeze({
    none: 1,
    nearest10bps: 10,
    nearest100bps: 100,
});
/** Every `indices.<id>.<field>` in the content, grouped by index id. */
export function groupIndexFields(values) {
    const out = new Map();
    for (const [key, value] of Object.entries(values)) {
        const parsed = parseIndexKey(key);
        if (parsed === null)
            continue;
        const fields = out.get(parsed.id) ?? new Map();
        fields.set(parsed.field, value);
        out.set(parsed.id, fields);
    }
    return out;
}
const rateOf = (v) => v !== undefined && v.kind === 'literal' ? v.valueBps : undefined;
const textOf = (v) => v !== undefined && v.kind === 'text' ? v.text : undefined;
const optionOf = (v) => v !== undefined && v.kind === 'enum' ? v.option : undefined;
const refOf = (v) => v !== undefined && v.kind === 'ref' ? v.ref : undefined;
/** The id an `indices.<id>` reference points at, or null. */
function referencedId(ref) {
    const m = /^indices\.([a-z0-9][a-z0-9-]{0,39})$/.exec(ref);
    return m === null ? null : m[1];
}
/**
 * Read one index instance, reporting every problem on the FIELD it sits on
 * (422 field paths downstream). Returns null when the instance cannot be read
 * at all, having pushed the reasons.
 */
function readIndex(id, fields, errors) {
    const key = (field) => `indices.${id}.${field}`;
    const name = textOf(fields.get('name'));
    if (name === undefined) {
        errors.push({
            key: key('name'),
            code: fields.has('name') ? 'wrong-kind' : 'missing-required',
            message: 'This index needs a name — it is how an expense chooses it.',
        });
    }
    else if (isUnsafeIndexName(name)) {
        errors.push({
            key: key('name'),
            code: 'not-allowed',
            message: `“${name}” cannot be used as an index name: it is a reserved word in the format these figures are stored in. Choose another name.`,
        });
    }
    const baseRaw = optionOf(fields.get('base'));
    const baseRef = refOf(fields.get('base'));
    let base;
    if (baseRef !== undefined) {
        const target = referencedId(baseRef);
        if (target === null) {
            errors.push({
                key: key('base'),
                code: 'unknown-index',
                message: `“${baseRef}” is not an index in this set. Choose another base.`,
            });
        }
        else {
            base = { index: target };
        }
    }
    else if (baseRaw === undefined) {
        errors.push({
            key: key('base'),
            code: fields.has('base') ? 'wrong-kind' : 'missing-required',
            message: 'Say what this index follows: a fixed rate, CPI, RPI, earnings or another index.',
        });
    }
    else if (!INDEX_BASE_OPTIONS.includes(baseRaw)) {
        errors.push({
            key: key('base'),
            code: 'not-allowed',
            message: `“${baseRaw}” is not a base this product understands. Choose a fixed rate, CPI, RPI, earnings or another index.`,
        });
    }
    else {
        base = baseRaw;
    }
    const rateBps = rateOf(fields.get('rate'));
    if (base === 'fixed' && rateBps === undefined) {
        errors.push({
            key: key('rate'),
            code: fields.has('rate') ? 'wrong-kind' : 'missing-required',
            message: 'An index on its own fixed rate needs that rate.',
        });
    }
    const spreadBps = rateOf(fields.get('spread')) ?? 0;
    const floorBps = rateOf(fields.get('floor'));
    const capBps = rateOf(fields.get('cap'));
    if (floorBps !== undefined && capBps !== undefined && capBps < floorBps) {
        errors.push({
            key: key('cap'),
            code: 'out-of-bounds',
            message: `The cap (${pct(capBps)}) is below the floor (${pct(floorBps)}). A cap must be at least the floor.`,
        });
    }
    const roundingRaw = optionOf(fields.get('rounding'));
    if (roundingRaw !== undefined &&
        !INDEX_ROUNDING_OPTIONS.includes(roundingRaw)) {
        errors.push({
            key: key('rounding'),
            code: 'not-allowed',
            message: `“${roundingRaw}” is not a rounding this product understands.`,
        });
    }
    if (name === undefined || base === undefined)
        return null;
    return {
        id,
        name,
        base,
        rateBps,
        spreadBps,
        floorBps,
        capBps,
        rounding: roundingRaw ?? 'none',
    };
}
/** The reference chain of the first cycle reachable from `start`, or null. */
function findCycle(start, edges) {
    const seen = [];
    let at = start;
    while (at !== null) {
        if (seen.includes(at))
            return [...seen.slice(seen.indexOf(at)), at];
        seen.push(at);
        at = edges.get(at) ?? null;
    }
    return null;
}
/**
 * Resolve the §5.1 economy group: RPI's basis, then every named index in
 * topological order.
 *
 * `rate = min(cap, max(floor, base + spread))` with absent bounds ignored
 * (§6.2), then rounding. A cycle, a reference to an index that is not in the
 * set, or a missing required figure is a BLOCKING error naming the whole chain
 * — never a quiet zero.
 */
export function resolveEconomy(values, inputs) {
    const errors = [];
    // --- RPI basis ---------------------------------------------------------
    const linkModeRaw = optionOf(values['economy.rpiLinkMode']);
    if (linkModeRaw !== undefined && !RPI_LINK_MODES.includes(linkModeRaw)) {
        errors.push({
            key: 'economy.rpiLinkMode',
            code: 'not-allowed',
            message: `“${linkModeRaw}” is not an RPI basis this product understands.`,
        });
    }
    const rpiLinked = linkModeRaw === 'linked';
    const rpiSpreadBps = rateOf(values['economy.rpiSpread']);
    if (rpiLinked && rpiSpreadBps === undefined) {
        errors.push({
            key: 'economy.rpiSpread',
            code: 'missing-required',
            message: 'Derived RPI needs the spread over CPI. Enter it, or hold RPI as its own figure.',
        });
    }
    if (rpiLinked && inputs.storedRpiBps !== undefined) {
        errors.push({
            key: 'economy.rpi',
            code: 'not-allowed',
            message: 'RPI is derived from CPI in this set, so it cannot also carry its own figure. Remove one of the two.',
        });
    }
    if (!rpiLinked && rpiSpreadBps !== undefined) {
        errors.push({
            key: 'economy.rpiSpread',
            code: 'not-allowed',
            message: 'A spread over CPI applies only when RPI is derived. Switch the basis, or remove the spread.',
        });
    }
    const rpiBps = rpiLinked ? inputs.cpiBps + (rpiSpreadBps ?? 0) : (inputs.storedRpiBps ?? 0);
    // --- the named indices -------------------------------------------------
    const grouped = groupIndexFields(values);
    const specs = new Map();
    for (const [id, fields] of grouped) {
        // A gated field on an instance is refused by the registry validator; this
        // resolver simply does not read one, so a gated figure can never reach a
        // rate even if validation were skipped.
        const spec = readIndex(id, fields, errors);
        if (spec !== null)
            specs.set(id, spec);
    }
    const byName = new Map();
    for (const spec of specs.values()) {
        // Trimmed and case-folded: "Care", "care " and "CARE" are one name to an
        // item choosing between them, so they are one name here.
        const normalised = normaliseIndexName(spec.name);
        const clash = byName.get(normalised);
        if (clash !== undefined) {
            errors.push({
                key: `indices.${spec.id}.name`,
                code: 'duplicate-name',
                message: `Another index in this set is already called “${spec.name}”. An expense chooses an index by name, so the two could not be told apart.`,
            });
        }
        else {
            byName.set(normalised, spec.id);
        }
    }
    const edges = new Map();
    for (const spec of specs.values()) {
        const target = typeof spec.base === 'object' ? spec.base.index : null;
        if (target !== null && !specs.has(target)) {
            errors.push({
                key: `indices.${spec.id}.base`,
                code: 'unknown-index',
                message: `“${spec.name}” is based on an index that is not in this set (${target}). Choose another base, or add that index back.`,
            });
            edges.set(spec.id, null);
            continue;
        }
        edges.set(spec.id, target);
    }
    const nameOf = (id) => specs.get(id)?.name ?? id;
    const reportedCycles = new Set();
    for (const id of specs.keys()) {
        const cycle = findCycle(id, edges);
        if (cycle === null)
            continue;
        // One cycle, reported once: the same loop entered from a different member
        // yields a rotated chain, so the MEMBERS are the identity, not the order.
        const members = [...new Set(cycle)].sort();
        const signature = members.join('|');
        if (reportedCycles.has(signature))
            continue;
        reportedCycles.add(signature);
        // Rotate to a fixed starting point so the SAME loop always reads the same
        // way, whatever order the keys arrived in.
        const start = cycle.indexOf(members[0]);
        const loop = [...cycle.slice(start, -1), ...cycle.slice(0, start), members[0]];
        errors.push({
            key: `indices.${loop[0]}.base`,
            code: 'cycle',
            message: `${loop.map(nameOf).join(' → ')}. These indices are based on each other in a loop; remove one of the links. Fields: ${members
                .map((c) => `indices.${c}.base`)
                .join(', ')}`,
        });
    }
    if (errors.length > 0)
        return { ok: false, errors };
    // --- resolve, deepest reference first ----------------------------------
    const resolved = new Map();
    const resolveOne = (spec) => {
        const existing = resolved.get(spec.id);
        if (existing !== undefined)
            return existing;
        let baseBps;
        let baseLabel;
        if (typeof spec.base === 'object') {
            const parent = resolveOne(specs.get(spec.base.index));
            baseBps = parent.rateBps;
            baseLabel = `${parent.name} ${pct(parent.rateBps)}`;
        }
        else if (spec.base === 'fixed') {
            baseBps = spec.rateBps ?? 0;
            baseLabel = 'fixed';
        }
        else if (spec.base === 'cpi') {
            baseBps = inputs.cpiBps;
            baseLabel = `CPI ${pct(inputs.cpiBps)}`;
        }
        else if (spec.base === 'rpi') {
            baseBps = rpiBps;
            baseLabel = `RPI ${pct(rpiBps)}`;
        }
        else {
            baseBps = inputs.earningsBps;
            baseLabel = `Earnings ${pct(inputs.earningsBps)}`;
        }
        const raw = baseBps + spec.spreadBps;
        const floored = spec.floorBps === undefined ? raw : Math.max(raw, spec.floorBps);
        const collared = spec.capBps === undefined ? floored : Math.min(floored, spec.capBps);
        const rateBps = roundBpsTo(collared, ROUNDING_STEP[spec.rounding]);
        const parts = [spec.base === 'fixed' ? pct(baseBps) : baseLabel];
        if (spec.spreadBps !== 0)
            parts.push(`${spec.spreadBps > 0 ? '+' : '−'} ${pct(Math.abs(spec.spreadBps))}`);
        if (spec.floorBps !== undefined)
            parts.push(`floor ${pct(spec.floorBps)}`);
        if (spec.capBps !== undefined)
            parts.push(`cap ${pct(spec.capBps)}`);
        if (spec.rounding !== 'none') {
            parts.push(`rounded to the nearest ${ROUNDING_STEP[spec.rounding] / 100}%`);
        }
        const entry = {
            id: spec.id,
            name: spec.name,
            rateBps,
            basis: parts.join(', '),
        };
        resolved.set(spec.id, entry);
        return entry;
    };
    for (const spec of specs.values())
        resolveOne(spec);
    return {
        ok: true,
        economy: {
            cpiBps: inputs.cpiBps,
            rpiBps,
            earningsBps: inputs.earningsBps,
            rpiLinked,
            // Stable order: by id, so a canonical hash of the resolution cannot move
            // with the order keys happen to arrive in.
            indices: [...resolved.values()].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)),
        },
    };
}
/** Every gated index field an instance carries, with its template. */
export function gatedIndexFields(values) {
    return Object.keys(values).filter((key) => {
        const parsed = parseIndexKey(key);
        if (parsed === null)
            return false;
        return assumptionDefinitionFor(key)?.status === 'gated';
    });
}
//# sourceMappingURL=indices.js.map